import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Download, RotateCcw, Flag, Receipt } from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import { MultiSelect, SelectOne, TextField } from '../components/Expenses/FilterBar';
import TotalsBar from '../components/Expenses/TotalsBar';
import SpendTrend from '../components/Expenses/SpendTrend';
import YearOverYear from '../components/Expenses/YearOverYear';
import BreakdownPanel from '../components/Expenses/BreakdownPanel';
import RankedBarChart from '../components/Expenses/RankedBarChart';
import CategoryMixTrend from '../components/Expenses/CategoryMixTrend';
import TransactionTable from '../components/Expenses/TransactionTable';
import ReviewFlags from '../components/Expenses/ReviewFlags';
import { fmtMoney, fmtMonth } from '../components/Expenses/format';
import PageHeader from '../components/Shared/PageHeader';
import { btn } from '../theme';

// Filters live in the URL so a view is shareable and survives a refresh.
const ARRAY_KEYS = ['months', 'years', 'cost_center_id', 'employee', 'supervisor', 'expense_item',
                    'spend_category', 'merchant', 'line_of_business', 'city_code'];
const SCALAR_KEYS = ['payment_type', 'search', 'min_amount', 'max_amount', 'start', 'end', 'preset'];
// Every key that narrows by date — the one group the YoY comparison must ignore.
const DATE_KEYS   = ['months', 'years', 'start', 'end', 'preset'];

// Year-over-year is built and working, but hidden: Workday expense history only
// becomes complete around Dec 2025 (Jan–Jul 2025 holds 27 transactions against
// 10,502 for the same months of 2026). Every comparison it can currently draw
// measures the missing history rather than a change in spend, and the component's
// own coverage guard would suppress the percentages anyway. Flip this to true
// once a full clean prior year exists — nothing else needs to change.
const SHOW_YEAR_OVER_YEAR = false;

const PRESETS = [
  { value: 'all',       label: 'All time' },
  { value: 'ytd',       label: 'Year to date' },
  { value: 'last_3m',   label: 'Last 3 months' },
  { value: 'last_6m',   label: 'Last 6 months' },
  { value: 'last_12m',  label: 'Last 12 months' },
  { value: 'last_year', label: 'Last calendar year' },
];

export default function ExpenseAnalysis() {
  const [sp, setSp] = useSearchParams();
  const [tab, setTab]   = useState('dashboard');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('line_date');
  const [dir, setDir]   = useState('desc');
  const [exporting, setExporting] = useState(false);

  // ── Filter state, read from / written to the URL ──────────────────────────
  const filters = useMemo(() => {
    const f = {};
    for (const k of ARRAY_KEYS)  f[k] = sp.get(k) ? sp.get(k).split(',').filter(Boolean) : [];
    for (const k of SCALAR_KEYS) f[k] = sp.get(k) || '';
    return f;
  }, [sp]);

  // Every mutation goes through setFilters so that changing SEVERAL keys at
  // once is a single URL update. Two back-to-back setFilter calls each rebuilt
  // from the same `sp` snapshot, so the second silently discarded the first —
  // which is why picking a month while a Period preset was active cleared the
  // preset and the month with it.
  const setFilters = (patch) => {
    const next = new URLSearchParams(sp);
    for (const [key, value] of Object.entries(patch)) {
      const empty = Array.isArray(value) ? value.length === 0 : !value || value === 'all';
      if (empty) next.delete(key);
      else next.set(key, Array.isArray(value) ? value.join(',') : value);
    }
    setSp(next, { replace: true });
    setPage(1);
  };

  const setFilter = (key, value) => setFilters({ [key]: value });

  const clearAll = () => { setSp(new URLSearchParams(), { replace: true }); setPage(1); };

  // Params sent to every endpoint — one object, so the row query, the totals,
  // the breakdowns and the export can never disagree about the selection.
  const params = useMemo(() => {
    const p = {};
    for (const k of ARRAY_KEYS)  if (filters[k].length) p[k] = filters[k].join(',');
    for (const k of SCALAR_KEYS) if (filters[k])        p[k] = filters[k];
    return p;
  }, [filters]);

  // The YoY endpoint drops date predicates server-side, so stripping them here
  // too keeps its query key stable — otherwise every month click would refetch
  // a result that cannot have changed.
  const yoyParams = useMemo(() => {
    const p = { ...params };
    for (const k of DATE_KEYS) delete p[k];
    return p;
  }, [params]);

  const activeCount = ARRAY_KEYS.reduce((a, k) => a + (filters[k].length ? 1 : 0), 0)
                    + SCALAR_KEYS.reduce((a, k) => a + (filters[k] ? 1 : 0), 0);

  const { data: opts, isLoading: optsLoading } = useQuery({
    queryKey: ['expense-filters'],
    queryFn:  () => api.get('/expenses/filters'),
  });

  // Land on the most recent month with data rather than five years of history —
  // an unfiltered all-time total is not a number anyone wants as a landing page.
  useEffect(() => {
    if (!opts?.months?.length) return;
    if ([...sp.keys()].length > 0) return;
    const latest = opts.months[0]?.month;
    if (latest) setSp(new URLSearchParams({ months: latest }), { replace: true });
  }, [opts]);            // eslint-disable-line react-hooks/exhaustive-deps

  const { data: summary, isFetching: summaryLoading } = useQuery({
    queryKey: ['expense-summary', params],
    queryFn:  () => api.get('/expenses/summary', { params }),
    keepPreviousData: true,
  });

  const { data: trend, isFetching: trendLoading } = useQuery({
    queryKey: ['expense-trend', params],
    queryFn:  () => api.get('/expenses/trend', { params }),
    keepPreviousData: true,
    enabled: tab === 'dashboard',
  });

  const { data: txns, isFetching: txnsLoading } = useQuery({
    queryKey: ['expense-transactions', params, page, sort, dir],
    queryFn:  () => api.get('/expenses/transactions', { params: { ...params, page, sort, dir, limit: 100 } }),
    keepPreviousData: true,
    enabled: tab === 'transactions',
  });

  function onSort(col) {
    if (sort === col) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(col); setDir('desc'); }
    setPage(1);
  }

  // Drill-down from a breakdown row into the matching filter.
  function drill(dimension, label) {
    const map = {
      cost_center:  () => {
        const cc = opts?.cost_centers?.find((c) => `${c.code} ${c.name}` === label);
        if (cc) setFilter('cost_center_id', [...new Set([...filters.cost_center_id, cc.id])]);
      },
      spend_category:   () => setFilter('spend_category',   [...new Set([...filters.spend_category, label])]),
      expense_item:     () => setFilter('expense_item',     [...new Set([...filters.expense_item, label])]),
      employee:         () => setFilter('employee',         [...new Set([...filters.employee, label])]),
      merchant:         () => setFilter('merchant',         [...new Set([...filters.merchant, label])]),
      line_of_business: () => setFilter('line_of_business', [...new Set([...filters.line_of_business, label])]),
      city_code:        () => setFilter('city_code',        [...new Set([...filters.city_code, label])]),
      month:            () => setFilters({ months: [...new Set([...filters.months, label])], preset: '' }),
      payment_type:     () => setFilter('payment_type', label === 'Reimbursement' ? 'reimbursement' : 'card'),
    };
    (map[dimension] || (() => {}))();
  }

  async function doExport() {
    setExporting(true);
    try {
      const qs = new URLSearchParams(params).toString();
      const blob = await api.getBlob(`/expenses/export?${qs}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `expense-analysis-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message || 'Export failed.');
    } finally {
      setExporting(false);
    }
  }

  // Plain-language description of the selection, shown on the totals bar.
  const description = useMemo(() => {
    const bits = [];
    if (filters.preset) bits.push(PRESETS.find((p) => p.value === filters.preset)?.label || filters.preset);
    if (filters.years.length) {
      bits.push(filters.years.length === 1 ? filters.years[0] : `${filters.years.length} years`);
    }
    if (filters.months.length) {
      bits.push(filters.months.length <= 3
        ? filters.months.map(fmtMonth).join(', ')
        : `${filters.months.length} months`);
    }
    if (filters.start || filters.end) bits.push(`${filters.start || '…'} → ${filters.end || '…'}`);
    const named = [
      ['employee', 'employee'], ['supervisor', 'manager'], ['expense_item', 'expense item'],
      ['spend_category', 'category'], ['merchant', 'merchant'], ['line_of_business', 'line of business'],
      ['city_code', 'city'],
    ];
    for (const [key, noun] of named) {
      if (filters[key].length === 1) bits.push(filters[key][0]);
      else if (filters[key].length > 1) bits.push(`${filters[key].length} ${noun}s`);
    }
    if (filters.cost_center_id.length) {
      bits.push(filters.cost_center_id.length === 1
        ? (() => { const cc = opts?.cost_centers?.find((c) => c.id === filters.cost_center_id[0]);
                   return cc ? `${cc.code} ${cc.name}` : '1 cost center'; })()
        : `${filters.cost_center_id.length} cost centers`);
    }
    if (filters.payment_type === 'card')          bits.push('company card only');
    if (filters.payment_type === 'reimbursement') bits.push('reimbursements only');
    if (filters.search) bits.push(`"${filters.search}"`);
    return bits.length ? bits.join(' · ') : 'All transactions, all time';
  }, [filters, opts]);

  if (optsLoading) return <LoadingSpinner />;

  const noData = !opts?.months?.length;

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Finance"
        title="Expense Analysis"
        subtitle="Workday expense report lines. Every total below reflects the full filtered set, not just the rows on screen."
      />

      {noData ? (
        <div style={s.emptyCard}>
          <Receipt size={28} color="#9CA3AF" />
          <div style={s.emptyMsg}>No expense data has been imported yet.</div>
        </div>
      ) : (
        <>
          {/* ── Filters ── */}
          <div style={s.filterCard}>
            <div style={s.filterGrid}>
              {/* Period preset first — it is the fastest way to a sensible
                  range, and clears the explicit month/year picks so the three
                  date controls can never silently contradict each other. */}
              <SelectOne label="Period" width={160}
                options={PRESETS}
                value={filters.preset || 'all'}
                onChange={(v) => setFilters({ preset: v, months: [], years: [], start: '', end: '' })} />
              <MultiSelect label="Year" width={130}
                options={(opts.years || []).map((y) => ({ value: y.year, label: y.year, count: y.n }))}
                value={filters.years}
                onChange={(v) => setFilters({ years: v, ...(v.length ? { preset: '' } : {}) })} />
              <MultiSelect label="Month" width={190}
                options={(opts.months || []).map((m) => ({ value: m.month, label: fmtMonth(m.month), count: m.n }))}
                value={filters.months}
                onChange={(v) => setFilters({ months: v, ...(v.length ? { preset: '' } : {}) })} />
              <MultiSelect label="Cost Center" width={210}
                options={(opts.cost_centers || []).map((c) => ({ value: c.id, label: `${c.code} ${c.name}` }))}
                value={filters.cost_center_id} onChange={(v) => setFilter('cost_center_id', v)} />
              <MultiSelect label="Employee" width={190}
                options={(opts.employees || []).map((e) => ({ value: e.name, label: e.name, count: e.n }))}
                value={filters.employee} onChange={(v) => setFilter('employee', v)} />
              <MultiSelect label="Manager" width={190}
                options={(opts.supervisors || []).map((e) => ({ value: e.name, label: e.name, count: e.n }))}
                value={filters.supervisor} onChange={(v) => setFilter('supervisor', v)} />
              <MultiSelect label="Expense Item" width={190}
                options={(opts.expense_items || []).map((e) => ({ value: e.name, label: e.name, count: e.n }))}
                value={filters.expense_item} onChange={(v) => setFilter('expense_item', v)} />
              <MultiSelect label="Spend Category" width={190}
                options={(opts.spend_categories || []).map((e) => ({ value: e.name, label: e.name, count: e.n }))}
                value={filters.spend_category} onChange={(v) => setFilter('spend_category', v)} />
              <MultiSelect label="Merchant" width={190}
                options={(opts.merchants || []).map((e) => ({ value: e.name, label: e.name, count: e.n }))}
                value={filters.merchant} onChange={(v) => setFilter('merchant', v)} />
              <SelectOne label="Payment Type" width={155}
                options={[{ value: 'all', label: 'All' },
                          { value: 'card', label: 'Company Card' },
                          { value: 'reimbursement', label: 'Reimbursement' }]}
                value={filters.payment_type || 'all'} onChange={(v) => setFilter('payment_type', v)} />
              <MultiSelect label="Line of Business" width={170}
                options={(opts.lines_of_business || []).map((e) => ({ value: e.name, label: e.name, count: e.n }))}
                value={filters.line_of_business} onChange={(v) => setFilter('line_of_business', v)} />
              <MultiSelect label="City" width={160}
                options={(opts.city_codes || []).map((e) => ({ value: e.name, label: e.name, count: e.n }))}
                value={filters.city_code} onChange={(v) => setFilter('city_code', v)} />
              <TextField label="Min $" width={90} type="number"
                value={filters.min_amount} onChange={(v) => setFilter('min_amount', v)} placeholder="0" />
              <TextField label="Max $" width={90} type="number"
                value={filters.max_amount} onChange={(v) => setFilter('max_amount', v)} placeholder="—" />
              <TextField label="Search" width={200}
                value={filters.search} onChange={(v) => setFilter('search', v)}
                placeholder="Memo, merchant, report…" />
            </div>

            <div style={s.filterActions}>
              <span style={s.filterCount}>
                {activeCount === 0 ? 'No filters applied' : `${activeCount} filter${activeCount !== 1 ? 's' : ''} applied`}
              </span>
              {activeCount > 0 && (
                <button style={s.linkBtn} onClick={clearAll}><RotateCcw size={12} /> Clear all</button>
              )}
              <div style={{ flex: 1 }} />
              <button style={s.exportBtn} onClick={doExport} disabled={exporting}>
                <Download size={13} /> {exporting ? 'Exporting…' : 'Export to Excel'}
              </button>
            </div>
          </div>

          {/* ── Tabs ── */}
          <div style={s.tabs}>
            {[['dashboard', 'Dashboard'], ['transactions', 'Transactions'], ['flags', 'Review Flags']].map(([k, label]) => (
              <button key={k} style={{ ...s.tab, ...(tab === k ? s.tabActive : {}) }} onClick={() => setTab(k)}>
                {k === 'flags' && <Flag size={12} style={{ marginRight: 4 }} />}
                {label}
              </button>
            ))}
          </div>

          {tab === 'dashboard' && (
            <div style={s.dashGrid}>
              <SpendTrend rows={trend?.rows} loading={trendLoading}
                          selectedMonths={filters.months}
                          onSelectMonth={(m) => {
                            const next = filters.months.includes(m)
                              ? filters.months.filter((x) => x !== m)
                              : [...filters.months, m];
                            // Clearing the preset alongside keeps the three date
                            // controls from silently intersecting each other.
                            setFilters({ months: next, ...(next.length ? { preset: '' } : {}) });
                          }} />
              {/* Year over year. Sits directly under the trend because it answers
                  the question the trend provokes: is this month high for us, or
                  just high for the year? Hidden until there is a full prior year
                  to compare against — see SHOW_YEAR_OVER_YEAR. */}
              {SHOW_YEAR_OVER_YEAR && <YearOverYear filterParams={yoyParams} />}

              {/* Ranked magnitude — one hue each, because the bar length is the
                  message and these categories have no natural order. */}
              <div style={s.panelRow}>
                <RankedBarChart
                  title="Spend by Category"
                  subtitle="Ranked, click a bar to filter"
                  dimension="spend_category" topN={10} unitLabel="categories"
                  mutedLabels={['<Unmapped>']}
                  filterParams={params} onDrill={drill} />
                <RankedBarChart
                  title="Spend by Expense Item"
                  subtitle="Ranked, click a bar to filter"
                  dimension="expense_item" topN={10} unitLabel="expense items"
                  filterParams={params} onDrill={drill} />
              </div>

              {/* Vendor spend. The reimbursement bucket is excluded rather than
                  charted: at $489K it is ~4x the largest real vendor and would
                  bury every one of them. The footer states the exclusion. */}
              <RankedBarChart
                title="Spend by Vendor"
                subtitle="Normalized merchants. Reimbursements have no vendor — shown as their own gray bar, not merged into Other."
                dimension="merchant" topN={15} height={420} unitLabel="vendors"
                mutedLabels={['Reimbursement (no vendor)']}
                filterParams={params} onDrill={drill} />

              {/* Mix over time — the one chart where the series ARE the subject,
                  so it carries the validated categorical palette. */}
              <CategoryMixTrend
                filterParams={params}
                dimension="spend_category"
                title="Category Mix Over Time"
                subtitle="Top 7 categories by total spend, remainder grouped as Other. Colours are fixed per category, so filtering never repaints them." />

              {/* Geography. Sits with the other organizational cuts rather than
                  up with the category charts, because "where" is a roll-up
                  question and the cost-center breakdown is its neighbour.

                  "No City" is EXCLUDED, not charted. It is not missing data —
                  it is the worktag for spend with no service city (corporate
                  and the like) — but it is 91% of all expense spend, $1.83M
                  against $27K for the largest real city. Charted, it is a bar
                  68x the next one and every actual city renders as an invisible
                  sliver. Excluding it server-side (rather than client-side)
                  recomputes the scope totals, so this chart still reconciles
                  internally, and the footer states the excluded dollar amount
                  so the slice this covers is never mistaken for the whole.

                  'None' stays muted-but-charted as a guard: today the worktag is
                  present on 11,782/11,782 rows, but a future extract with a
                  blank one should be visible, not silently absent.

                  topN is 20, not the usual 10-15: excluding "No City" leaves
                  only 52 cities and a long flat tail, so 12 bars would push 35%
                  of the charted spend into Other. 20 covers 85% and holds Other
                  at 15% — under the threshold where the component escalates its
                  footnote to a warning. */}
              <RankedBarChart
                title="Spend by City"
                subtitle="Lines carrying a service-city worktag, ranked by spend. Excludes “No City” — corporate and other spend with no city attached, which is the large majority of the total. Click a bar to filter."
                dimension="city_code" topN={20} height={360} unitLabel="cities"
                excludeLabels={['No City']} excludeNote="tagged “No City”"
                mutedLabels={['None']}
                filterParams={params} onDrill={drill} />

              <div style={s.panelRow}>
                <BreakdownPanel filterParams={params} onDrill={drill} defaultDimension="cost_center" />
                <BreakdownPanel filterParams={params} onDrill={drill} defaultDimension="employee" />
              </div>
            </div>
          )}

          {tab === 'transactions' && (
            <TransactionTable data={txns} loading={txnsLoading} sort={sort} dir={dir}
                              onSort={onSort} page={page} onPage={setPage} />
          )}

          {tab === 'flags' && <ReviewFlags filterParams={params} />}

          {/* Sticky bottom line — always the whole filtered set. */}
          <TotalsBar totals={summary?.totals} shareOfTotal={summary?.share_of_total}
                     description={description} loading={summaryLoading} />
        </>
      )}
    </div>
  );
}

const s = {
  page:  { maxWidth: '1500px', margin: '0 auto', paddingBottom: '8px' },
  filterCard: { background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '14px 16px', marginBottom: '14px' },
  filterGrid: { display: 'flex', flexWrap: 'wrap', gap: '10px 14px' },
  filterActions: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #F3F4F6' },
  filterCount: { fontSize: '11px', color: '#6B7280' },
  linkBtn:     { ...btn.linkXs },
  exportBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '7px 13px', background: '#1B2A4A', color: '#fff',
    border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 500, cursor: 'pointer',
  },
  tabs: { display: 'flex', gap: '4px', marginBottom: '14px', borderBottom: '1px solid #E5E7EB' },
  tab: {
    display: 'inline-flex', alignItems: 'center',
    padding: '8px 14px', background: 'none', border: 'none', borderBottom: '2px solid transparent',
    fontSize: '12.5px', color: '#6B7280', cursor: 'pointer', marginBottom: '-1px',
  },
  tabActive: { color: '#1B2A4A', fontWeight: 600, borderBottomColor: '#1B2A4A' },
  dashGrid:  { display: 'flex', flexDirection: 'column', gap: '14px' },
  panelRow:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '14px' },
  emptyCard: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: '12px', padding: '64px 24px', background: '#FFFFFF',
    border: '1px solid #E5E7EB', borderRadius: '10px',
  },
  emptyMsg: { fontSize: '13px', color: '#6B7280' },
};
